import { ARENA_RAKE_BPS_DIVISOR, ARENA_RAKE_BPS_STEP } from "./rake";
import { z } from "zod";

// ──────────────────────────── Cede Arena KOL managers ──────────────────────────
//
// A KOL manager is an agency that recruits KOLs. Each KOL is linked to one manager
// through the referral system (the manager is the KOL's referrer), and the manager
// decides how the platform rake on the KOL's referees' buy-ins is split:
// `kolShareBps + managerShareBps <= maxShareBps` (Solo), Cede keeps the rest.
// The split replaces the KOL's ladder or deal while the link lasts.

/** Days an invite link stays usable. */
export const ARENA_KOL_INVITE_TTL_DAYS = 7;

/** Who may accept an invite, matched against the signed-in account's verified email. Stored lowercased. */
export const ArenaKolInviteEmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email({ error: "Enter a valid email address" }).max(254));

export const ARENA_KOL_INVITE_STATUS = {
  ACTIVE: "ACTIVE",
  EXPIRED: "EXPIRED",
  CONSUMED: "CONSUMED",
  REVOKED: "REVOKED",
} as const;
export type ARENA_KOL_INVITE_STATUS = (typeof ARENA_KOL_INVITE_STATUS)[keyof typeof ARENA_KOL_INVITE_STATUS];

const ArenaKolInviteStatusSchema = z.enum([
  ARENA_KOL_INVITE_STATUS.ACTIVE,
  ARENA_KOL_INVITE_STATUS.EXPIRED,
  ARENA_KOL_INVITE_STATUS.CONSUMED,
  ARENA_KOL_INVITE_STATUS.REVOKED,
]);

/** Free-text on a CMS unlink, reassign or role change. */
export const ARENA_KOL_MANAGER_NOTE_MAX = 500;

/** Windows the dashboard's volume chart can be read over. */
export const ARENA_KOL_VOLUME_WINDOW_DAYS = [7, 30, 90] as const;

/** A share of the platform rake, on the rake's own 25 bps grid. */
export const ArenaKolShareBpsSchema = z
  .number()
  .int()
  .min(0, { error: "a share must be at least 0% of the platform rake" })
  .max(ARENA_RAKE_BPS_DIVISOR, { error: "a share must be at most 100% of the platform rake" })
  .refine((bps) => bps % ARENA_RAKE_BPS_STEP === 0, { error: "a share must be a multiple of 0.25% (25 bps)" });

/** The cap against `maxShareBps` is the API's to check: it is a live Solo setting. */
export const ArenaKolSplitSchema = z.object({
  kolShareBps: ArenaKolShareBpsSchema,
  managerShareBps: ArenaKolShareBpsSchema,
});
export type ArenaKolSplit = z.infer<typeof ArenaKolSplitSchema>;

export const ARENA_KOL_LINK_ACTION = {
  LINKED: "LINKED",
  SPLIT_CHANGED: "SPLIT_CHANGED",
  UNLINKED: "UNLINKED",
} as const;
export type ARENA_KOL_LINK_ACTION = (typeof ARENA_KOL_LINK_ACTION)[keyof typeof ARENA_KOL_LINK_ACTION];

/** Who made a link change: the manager from the dashboard, the KOL accepting an invite, or ops in Solo. */
export const ARENA_KOL_LINK_ACTOR = {
  MANAGER: "MANAGER",
  KOL: "KOL",
  CMS: "CMS",
} as const;
export type ARENA_KOL_LINK_ACTOR = (typeof ARENA_KOL_LINK_ACTOR)[keyof typeof ARENA_KOL_LINK_ACTOR];

/** One entry of the link audit trail. Splits are null on an UNLINKED entry. */
export const ArenaKolLinkEventDtoSchema = z.object({
  id: z.number().int().positive(),
  managerHubUserId: z.number().int().positive(),
  kolHubUserId: z.number().int().positive(),
  action: z.enum([ARENA_KOL_LINK_ACTION.LINKED, ARENA_KOL_LINK_ACTION.SPLIT_CHANGED, ARENA_KOL_LINK_ACTION.UNLINKED]),
  actor: z.enum([ARENA_KOL_LINK_ACTOR.MANAGER, ARENA_KOL_LINK_ACTOR.KOL, ARENA_KOL_LINK_ACTOR.CMS]),
  kolShareBps: z.number().int().nullable(),
  managerShareBps: z.number().int().nullable(),
  note: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export type ArenaKolLinkEventDto = z.infer<typeof ArenaKolLinkEventDtoSchema>;

// ─────────────────────────────── Arena-facing DTOs ────────────────────────────

/** Response for `GET /v1/cede-arena/kol-manager/status`: drives the nav entries. Both false while the programme is off. */
export const GetArenaKolManagerStatusResponseSchema = z.object({
  isKolManager: z.boolean(),
  /** Linked to a KOL manager, so their referrals page is their KOL referrals. */
  isKol: z.boolean(),
});
export type GetArenaKolManagerStatusResponse = z.infer<typeof GetArenaKolManagerStatusResponseSchema>;

/** Money figures are decimal USDT strings and count only what accrued while linked to this manager. */
export const ArenaKolDtoSchema = ArenaKolSplitSchema.extend({
  hubUserId: z.number().int().positive(),
  email: z.string().nullable(),
  /** The KOL's own referral code, minted when they accepted. */
  code: z.string().nullable(),
  /** When the KOL joined this manager's roster: their accept, or a Solo reassign. */
  linkedAt: z.coerce.date(),
  refereeCount: z.number().int().nonnegative(),
  refereeVolumeUsdt: z.string(),
  rakeUsdt: z.string(),
  kolEarnedUsdt: z.string(),
  managerEarnedUsdt: z.string(),
});
export type ArenaKolDto = z.infer<typeof ArenaKolDtoSchema>;

/** An invite the KOL has not accepted yet: a pending row of the roster. */
export const ArenaKolInviteDtoSchema = ArenaKolSplitSchema.extend({
  token: z.string(),
  email: z.string(),
  /** Always ACTIVE or EXPIRED on the dashboard, where only pending invites are listed. */
  status: ArenaKolInviteStatusSchema,
  createdAt: z.coerce.date(),
  expiresAt: z.coerce.date(),
});
export type ArenaKolInviteDto = z.infer<typeof ArenaKolInviteDtoSchema>;

/** Response for `GET /v1/cede-arena/kol-manager` and for its claim POST. KOLs by referee volume, biggest first. */
export const GetArenaKolManagerDashboardResponseSchema = z.object({
  /** The manager's own referral code. */
  code: z.string().nullable(),
  /** The Solo cap on `kolShareBps + managerShareBps`. */
  maxShareBps: z.number().int(),
  pendingRewardsUsdt: z.string(),
  claimedRewardsUsdt: z.string(),
  totals: z.object({
    refereeVolumeUsdt: z.string(),
    rakeUsdt: z.string(),
    kolEarnedUsdt: z.string(),
    managerEarnedUsdt: z.string(),
  }),
  kols: z.array(ArenaKolDtoSchema),
  /** Unused, unrevoked invites, expired ones included so they can be sent again. Newest first. */
  invites: z.array(ArenaKolInviteDtoSchema),
});
export type GetArenaKolManagerDashboardResponse = z.infer<typeof GetArenaKolManagerDashboardResponseSchema>;

/** Query for `GET /v1/cede-arena/kol-manager/volume`. */
export const GetArenaKolManagerVolumeQuerySchema = z.object({
  days: z.coerce
    .number()
    .int()
    .refine((days) => (ARENA_KOL_VOLUME_WINDOW_DAYS as readonly number[]).includes(days), {
      error: `days must be one of ${ARENA_KOL_VOLUME_WINDOW_DAYS.join(", ")}`,
    }),
});
export type GetArenaKolManagerVolumeQuery = z.infer<typeof GetArenaKolManagerVolumeQuerySchema>;

/** Response for `GET /v1/cede-arena/kol-manager/volume`: one point per (UTC day, KOL) with volume, oldest first. */
export const GetArenaKolManagerVolumeResponseSchema = z.object({
  points: z.array(
    z.object({
      /** `YYYY-MM-DD`, UTC. */
      date: z.string(),
      kolHubUserId: z.number().int().positive(),
      kolEmail: z.string().nullable(),
      volumeUsdt: z.string(),
    }),
  ),
});
export type GetArenaKolManagerVolumeResponse = z.infer<typeof GetArenaKolManagerVolumeResponseSchema>;

/** Response for `GET /v1/cede-arena/kol-manager/kols/detail?kolHubUserId=`. */
export const GetArenaKolDetailResponseSchema = z.object({
  /** The KOL's referees, by volume accrued under this manager, biggest first. */
  referees: z.array(
    z.object({
      hubUserId: z.number().int().positive(),
      nickname: z.string().nullable(),
      attributedAt: z.coerce.date(),
      volumeUsdt: z.string(),
      kolEarnedUsdt: z.string(),
      managerEarnedUsdt: z.string(),
    }),
  ),
  /** This manager's link history with the KOL, newest first. Solo's notes are ops-internal, so not sent. */
  history: z.array(ArenaKolLinkEventDtoSchema.omit({ note: true })),
});
export type GetArenaKolDetailResponse = z.infer<typeof GetArenaKolDetailResponseSchema>;

/** Body for `PUT /v1/cede-arena/kol-manager/kols/split`. */
export const UpdateArenaKolSplitBodySchema = ArenaKolSplitSchema.extend({
  kolHubUserId: z.number().int().positive(),
});
export type UpdateArenaKolSplitBody = z.infer<typeof UpdateArenaKolSplitBodySchema>;

/** Response for the KOL writes (split, `DELETE /kols?kolHubUserId=`): which KOL changed. */
export const ArenaKolWriteResponseSchema = z.object({
  kolHubUserId: z.number().int().positive(),
});
export type ArenaKolWriteResponse = z.infer<typeof ArenaKolWriteResponseSchema>;

/**
 * Body for `POST /v1/cede-arena/kol-manager/invites`: the KOL's email and split. Replaces any
 * pending invite of this manager for the same email. Response is the invite; `DELETE ?token=`
 * returns the revoked one.
 */
export const CreateArenaKolInviteBodySchema = ArenaKolSplitSchema.extend({
  email: ArenaKolInviteEmailSchema,
});
export type CreateArenaKolInviteBody = z.infer<typeof CreateArenaKolInviteBodySchema>;

/** An invite's status, with the email only an account holding it can accept; null once no longer ACTIVE. */
const ArenaInviteEmailByStatusSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal(ARENA_KOL_INVITE_STATUS.ACTIVE), email: z.string() }),
  z.object({ status: ArenaKolInviteStatusSchema.exclude([ARENA_KOL_INVITE_STATUS.ACTIVE]), email: z.null() }),
]);

/** Response for `GET /v1/cede-arena/kol-invites?token=`: what the invitee sees before joining. */
export const GetArenaKolInviteResponseSchema = ArenaKolSplitSchema.extend({
  managerNickname: z.string().nullable(),
  expiresAt: z.coerce.date(),
}).and(ArenaInviteEmailByStatusSchema);
export type GetArenaKolInviteResponse = z.infer<typeof GetArenaKolInviteResponseSchema>;

/** An invite token, as every invite route takes it. */
export const ArenaKolInviteTokenSchema = z.string().min(1).max(128);

/** Body for `POST /v1/cede-arena/kol-invites/accept`. Response is the invite as `GET` returns it. */
export const AcceptArenaKolInviteBodySchema = z.object({
  token: ArenaKolInviteTokenSchema,
});
export type AcceptArenaKolInviteBody = z.infer<typeof AcceptArenaKolInviteBodySchema>;

/**
 * Response for `GET /v1/cede-arena/kol-manager-role-invites?token=`: a Solo invite to become a
 * KOL manager, as its recipient sees it before signing in. The Solo note stays internal.
 */
export const GetArenaKolManagerRoleInviteResponseSchema = z
  .object({ expiresAt: z.coerce.date() })
  .and(ArenaInviteEmailByStatusSchema);
export type GetArenaKolManagerRoleInviteResponse = z.infer<typeof GetArenaKolManagerRoleInviteResponseSchema>;

/** Body for `POST /v1/cede-arena/kol-manager-role-invites/accept`. Response is the invite as `GET` returns it. */
export const AcceptArenaKolManagerRoleInviteBodySchema = AcceptArenaKolInviteBodySchema;
export type AcceptArenaKolManagerRoleInviteBody = z.infer<typeof AcceptArenaKolManagerRoleInviteBodySchema>;

// ──────────────────────────────────── CMS DTOs ─────────────────────────────────

const CmsNoteSchema = z.string().trim().min(1, { error: "a note is required" }).max(ARENA_KOL_MANAGER_NOTE_MAX);

export const CmsArenaKolManagerDtoSchema = z.object({
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  defiAddress: z.string().nullable(),
  email: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.coerce.date(),
  kolCount: z.number().int().nonnegative(),
  /** Pending + claimed manager rewards. */
  managerEarnedUsdt: z.string(),
});
export type CmsArenaKolManagerDto = z.infer<typeof CmsArenaKolManagerDtoSchema>;

/** Response for `GET /v1/cms/arena-kol-managers/get-by-page`: newest first. */
export const ListCmsArenaKolManagersResponseSchema = z.object({
  items: z.array(CmsArenaKolManagerDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ListCmsArenaKolManagersResponse = z.infer<typeof ListCmsArenaKolManagersResponseSchema>;

/** Query for `DELETE /v1/cms/arena-kol-managers`: revokes the role and unlinks every KOL. */
export const RevokeArenaKolManagerQuerySchema = z.object({
  hubUserId: z.coerce.number().int().positive(),
  note: CmsNoteSchema,
});
export type RevokeArenaKolManagerQuery = z.infer<typeof RevokeArenaKolManagerQuerySchema>;

/** Response for the manager role writes. */
export const ArenaKolManagerWriteResponseSchema = z.object({
  hubUserId: z.number().int().positive(),
});
export type ArenaKolManagerWriteResponse = z.infer<typeof ArenaKolManagerWriteResponseSchema>;

export const CmsArenaKolDtoSchema = ArenaKolSplitSchema.extend({
  kolHubUserId: z.number().int().positive(),
  nickname: z.string().nullable(),
  defiAddress: z.string().nullable(),
  email: z.string().nullable(),
  linkedAt: z.coerce.date(),
});
export type CmsArenaKolDto = z.infer<typeof CmsArenaKolDtoSchema>;

/** Response for `GET /v1/cms/arena-kol-managers/kols?managerHubUserId=`: newest link first. */
export const ListCmsArenaKolsResponseSchema = z.object({
  items: z.array(CmsArenaKolDtoSchema),
});
export type ListCmsArenaKolsResponse = z.infer<typeof ListCmsArenaKolsResponseSchema>;

/** Body for `POST /v1/cms/arena-kol-managers/kols/unlink`. */
export const UnlinkCmsArenaKolBodySchema = z.object({
  kolHubUserId: z.number().int().positive(),
  note: CmsNoteSchema,
});
export type UnlinkCmsArenaKolBody = z.infer<typeof UnlinkCmsArenaKolBodySchema>;

/** Body for `POST /v1/cms/arena-kol-managers/kols/reassign`. The KOL keeps their split. */
export const ReassignCmsArenaKolBodySchema = z.object({
  kolHubUserId: z.number().int().positive(),
  managerHubUserId: z.number().int().positive(),
  note: CmsNoteSchema,
});
export type ReassignCmsArenaKolBody = z.infer<typeof ReassignCmsArenaKolBodySchema>;

/** Query for `GET /v1/cms/arena-kol-managers/events`: at least one of the two ids. */
export const ListCmsArenaKolLinkEventsQuerySchema = z
  .object({
    managerHubUserId: z.coerce.number().int().positive().optional(),
    kolHubUserId: z.coerce.number().int().positive().optional(),
    page: z.coerce.number().int().nonnegative().default(0),
  })
  .refine((query) => query.managerHubUserId != null || query.kolHubUserId != null, {
    error: "managerHubUserId or kolHubUserId is required",
  });
export type ListCmsArenaKolLinkEventsQuery = z.infer<typeof ListCmsArenaKolLinkEventsQuerySchema>;

export const CMS_ARENA_KOL_LINK_EVENTS_PAGE_SIZE = 50;

/** Response for `GET /v1/cms/arena-kol-managers/events`: newest first. */
export const ListCmsArenaKolLinkEventsResponseSchema = z.object({
  items: z.array(ArenaKolLinkEventDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ListCmsArenaKolLinkEventsResponse = z.infer<typeof ListCmsArenaKolLinkEventsResponseSchema>;

/** A Solo invite to become a KOL manager, for the account holding `email`. Single-use, {@link ARENA_KOL_INVITE_TTL_DAYS} days. */
export const CmsArenaKolManagerRoleInviteDtoSchema = z.object({
  token: z.string(),
  email: z.string(),
  /** Carried onto the manager role when the invite is accepted. */
  note: z.string().nullable(),
  status: ArenaKolInviteStatusSchema,
  createdAt: z.coerce.date(),
  expiresAt: z.coerce.date(),
  consumedAt: z.coerce.date().nullable(),
  consumedByHubUserId: z.number().int().positive().nullable(),
  consumedByNickname: z.string().nullable(),
});
export type CmsArenaKolManagerRoleInviteDto = z.infer<typeof CmsArenaKolManagerRoleInviteDtoSchema>;

/**
 * Body for `POST /v1/cms/arena-kol-managers/role-invites`. Replaces any pending invite for the
 * same email. Response is the invite; `DELETE ?token=` returns the revoked one.
 */
export const CreateCmsArenaKolManagerRoleInviteBodySchema = z.object({
  email: ArenaKolInviteEmailSchema,
  note: z.string().max(ARENA_KOL_MANAGER_NOTE_MAX).nullable(),
});
export type CreateCmsArenaKolManagerRoleInviteBody = z.infer<typeof CreateCmsArenaKolManagerRoleInviteBodySchema>;

/** Response for `GET /v1/cms/arena-kol-managers/role-invites?page=&size=`: newest first, every status. */
export const ListCmsArenaKolManagerRoleInvitesResponseSchema = z.object({
  items: z.array(CmsArenaKolManagerRoleInviteDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ListCmsArenaKolManagerRoleInvitesResponse = z.infer<typeof ListCmsArenaKolManagerRoleInvitesResponseSchema>;
